import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Mountain, ArrowUpDown, TrendingDown, TrendingUp, Route, Droplets, TreePine } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ElevationProfile({ gpxFilename, color = '#d946ef', name = '' }) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);

  useEffect(() => {
    if (!gpxFilename) return;
    const fn = gpxFilename.replace('/api/gpx/', '');
    fetch(`${API}/api/gpx/${fn}/profile`)
      .then(r => r.json())
      .then(data => { if (data.points) setProfile(data); })
      .catch(() => {});
  }, [gpxFilename]);

  const handleMouseMove = useCallback((e) => {
    if (!svgRef.current || !profile?.points?.length) return;
    const rect = svgRef.current.getBoundingClientRect();
    const svgW = rect.width;
    const pxRatio = 50 / 800; // PX/W ratio
    const chartRatio = 740 / 800; // chartW/W ratio
    const x = e.clientX - rect.left;
    const pct = Math.max(0, Math.min(1, (x / svgW - pxRatio) / chartRatio));
    const idx = Math.round(pct * (profile.points.length - 1));
    const pt = profile.points[idx];
    if (pt) setHover({ x: pct, d: pt.d, a: pt.a });
  }, [profile]);

  const handleTouchMove = useCallback((e) => {
    if (!svgRef.current || !profile?.points?.length) return;
    const touch = e.touches[0];
    const rect = svgRef.current.getBoundingClientRect();
    const svgW = rect.width;
    const pxRatio = 50 / 800;
    const chartRatio = 740 / 800;
    const x = touch.clientX - rect.left;
    const pct = Math.max(0, Math.min(1, (x / svgW - pxRatio) / chartRatio));
    const idx = Math.round(pct * (profile.points.length - 1));
    const pt = profile.points[idx];
    if (pt) setHover({ x: pct, d: pt.d, a: pt.a });
  }, [profile]);

  if (!profile) return null;

  const { points, stats } = profile;
  if (!points.length) return null;

  const W = 800, H = 220, PX = 50, PY = 15;
  const minAlt = stats.altMin - 10;
  const maxAlt = stats.altMax + 10;
  const maxDist = points[points.length - 1].d;
  const chartW = W - PX - 10;
  const chartH = H - PY - 30;

  const toX = (d) => PX + (d / maxDist) * chartW;
  const toY = (a) => PY + (1 - (a - minAlt) / (maxAlt - minAlt)) * chartH;

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(p.d).toFixed(1)},${toY(p.a).toFixed(1)}`).join(' ');
  const areaD = pathD + ` L${toX(points[points.length - 1].d).toFixed(1)},${PY + chartH} L${PX},${PY + chartH} Z`;

  const gridLines = [];
  const altRange = maxAlt - minAlt;
  const altStep = altRange <= 40 ? 10 : altRange <= 100 ? 20 : 50;
  for (let a = Math.ceil(minAlt / altStep) * altStep; a <= maxAlt; a += altStep) {
    gridLines.push(a);
  }

  return (
    <div className="space-y-4" data-testid={`elevation-profile-${gpxFilename?.replace(/[^a-z0-9]/gi, '-')}`}>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="elevation-stats">
        <Card className="border-0 shadow-md bg-zinc-50">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: color + '20' }}>
              <Route className="h-4 w-4" style={{ color }} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{t('ip.elevDistance')}</p>
              <p className="text-lg font-bold text-zinc-900">{stats.distance.toFixed(2).replace('.', ',')} km</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md bg-zinc-50">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-50">
              <ArrowUpDown className="h-4 w-4 text-emerald-600" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{t('ip.elevDenivele')}</p>
              <p className="text-lg font-bold text-zinc-900">
                <span className="text-emerald-600">+{stats.elevGain}</span>
                <span className="text-zinc-300 mx-1">/</span>
                <span className="text-red-500">-{stats.elevLoss}</span>
                <span className="text-xs font-normal text-zinc-400 ml-1">m</span>
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md bg-zinc-50">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50">
              <TrendingDown className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{t('ip.elevAltMin')}</p>
              <p className="text-lg font-bold text-zinc-900">{stats.altMin} <span className="text-xs font-normal text-zinc-400">m</span></p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md bg-zinc-50">
          <CardContent className="p-3 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-50">
              <TrendingUp className="h-4 w-4 text-red-500" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{t('ip.elevAltMax')}</p>
              <p className="text-lg font-bold text-zinc-900">{stats.altMax} <span className="text-xs font-normal text-zinc-400">m</span></p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Terrain Info */}
      <div className="flex flex-wrap gap-4 text-sm text-zinc-500">
        <span className="flex items-center gap-1.5"><TreePine className="h-3.5 w-3.5 text-lime-600" />{t('ip.elevTerrain')}</span>
        <span className="flex items-center gap-1.5"><Droplets className="h-3.5 w-3.5 text-blue-400" />{t('ip.elevWater')}</span>
      </div>

      {/* Elevation Chart */}
      <Card className="border-0 shadow-lg overflow-hidden" data-testid="elevation-chart">
        <CardContent className="p-0">
          <div className="relative bg-white" style={{ touchAction: 'none' }}>
            <svg
              ref={svgRef}
              viewBox={`0 0 ${W} ${H + 25}`}
              className="w-full h-auto cursor-crosshair"
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setHover(null)}
              onTouchMove={handleTouchMove}
              onTouchEnd={() => setHover(null)}
              preserveAspectRatio="xMidYMid meet"
            >
              {/* Grid lines */}
              {gridLines.map(a => (
                <g key={a}>
                  <line x1={PX} y1={toY(a)} x2={PX + chartW} y2={toY(a)} stroke="#f4f4f5" strokeWidth="1" />
                  <text x={PX - 5} y={toY(a) + 4} fill="#a1a1aa" fontSize="11" fontFamily="system-ui" textAnchor="end">{a}</text>
                </g>
              ))}

              {/* Distance axis labels */}
              {[0, 0.25, 0.5, 0.75, 1].map(pct => {
                const d = pct * maxDist;
                return (
                  <text key={pct} x={toX(d)} y={PY + chartH + 16} fill="#a1a1aa" fontSize="11" textAnchor="middle" fontFamily="system-ui">
                    {d.toFixed(1)}
                  </text>
                );
              })}

              {/* Gradient fill */}
              <defs>
                <linearGradient id={`grad-${gpxFilename}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity="0.35" />
                  <stop offset="100%" stopColor={color} stopOpacity="0.03" />
                </linearGradient>
              </defs>
              <path d={areaD} fill={`url(#grad-${gpxFilename})`} />
              <path d={pathD} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" />

              {/* Hover cursor */}
              {hover && (
                <g>
                  <line
                    x1={PX + hover.x * chartW} y1={PY}
                    x2={PX + hover.x * chartW} y2={PY + chartH}
                    stroke={color} strokeWidth="1.5" strokeDasharray="4,3" opacity="0.6"
                  />
                  <circle cx={PX + hover.x * chartW} cy={toY(hover.a)} r="5" fill="white" stroke={color} strokeWidth="2.5" />
                  {/* Tooltip */}
                  <rect
                    x={Math.min(PX + hover.x * chartW + 10, W - 130)}
                    y={Math.max(toY(hover.a) - 40, 5)}
                    width="120" height="34" rx="6" fill="white"
                    stroke="#e4e4e7" strokeWidth="1" filter="drop-shadow(0 2px 4px rgba(0,0,0,0.08))"
                  />
                  <text
                    x={Math.min(PX + hover.x * chartW + 18, W - 122)}
                    y={Math.max(toY(hover.a) - 22, 18)}
                    fontSize="11" fill="#71717a" fontFamily="system-ui"
                  >
                    {t('ip.elevDistTooltip')} {hover.d.toFixed(1)} km
                  </text>
                  <text
                    x={Math.min(PX + hover.x * chartW + 18, W - 122)}
                    y={Math.max(toY(hover.a) - 10, 30)}
                    fontSize="12" fill="#18181b" fontWeight="bold" fontFamily="system-ui"
                  >
                    {t('ip.elevAltTooltip')} {Math.round(hover.a)} m
                  </text>
                </g>
              )}
            </svg>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
